import { lazy, createElement, type ComponentType, type FunctionComponent } from 'react';

type AnyProps = Record<string, unknown>;

export type PreloadableComponent = FunctionComponent<AnyProps> & {
  preload: () => Promise<void>;
};

/**
 * Code-split route component that can be resolved ahead of render.
 *
 * React.lazy always suspends on its first render pass, even when the module is
 * already in the module cache. During hydration that suspension trips the
 * Suspense boundary and React discards the prerendered DOM (error #423). Once
 * preload() has run, this wrapper renders the real component synchronously, so
 * the first hydration pass matches the prerendered markup.
 */
export function lazyWithPreload(
  importer: () => Promise<{ default: ComponentType<AnyProps> }>,
): PreloadableComponent {
  let Loaded: ComponentType<AnyProps> | null = null;

  const Lazy = lazy(async () => {
    const mod = await importer();
    Loaded = mod.default;
    return mod;
  });

  const Wrapper: PreloadableComponent = Object.assign(
    (props: AnyProps) => createElement(Loaded ?? Lazy, props),
    { preload: async () => {} },
  );

  Wrapper.preload = async () => {
    if (!Loaded) Loaded = (await importer()).default;
  };

  return Wrapper;
}
